// src/pages/resources/ChapterMcqTab.tsx
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, IconButton, Card, CardContent, Grid,
    Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
    LinearProgress, Tabs, Tab, InputAdornment, Checkbox, Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    CheckCircle as CorrectIcon,
    Cancel as WrongIcon,
    DeleteOutline, // Added missing import
    CloudUpload as UploadIcon,
    Link as LinkIcon,
    Movie as VideoIcon,
    YouTube as YouTubeIcon,
    Title as TitleIcon,
    Description as DescriptionIcon,
    HelpOutline as QuestionIcon,
    CheckCircle as CheckIcon,
    Image as ImageIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
    createMcqGroupWithUpload,
    updateMcqGroupWithUpload,
    deleteMcqGroup,
    setMcqVideoByUrl,
    uploadMcqVideo,
    deleteMcqVideo,
    McqOptionDto
} from '../../services/chapterService';

// --- Styles ---
const css = {
    primary: '#174143',
    primaryDark: '#0f2d2f',
    primaryLight: '#1f5961',
    primaryAccent: '#3d9ea4',
    bgGradientStart: '#e0f2f2',
    bgGradientEnd: '#3d9ea4',
};

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    border: `1px solid rgba(23, 65, 67, 0.1)`,
    height: '100%',
    position: 'relative',
    overflow: 'visible'
}));

const GradientButton = styled(Button)({
    background: `linear-gradient(to right, ${css.primary}, ${css.primaryLight})`,
    color: 'white',
    padding: '10px 24px',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
        background: `linear-gradient(to right, ${css.primaryDark}, ${css.primary})`,
    },
});

function CloudUploadIcon(props: any) {
    return <UploadIcon {...props} />;
}

export default function ChapterMcqTab({ chapterId, data, refreshData }: { chapterId: number, data: any[], refreshData: () => void }) {
    // --- Video Management State ---
    const [mode, setMode] = useState<'URL' | 'FILE'>('URL');
    const [urlForm, setUrlForm] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [meta, setMeta] = useState({ title: '', description: '', question: '' });

    // Status
    const [loadingVideo, setLoadingVideo] = useState(false);
    const [progress, setProgress] = useState(0);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // --- MCQ Group Management State ---
    const [openDialog, setOpenDialog] = useState(false);
    const [editingGroup, setEditingGroup] = useState<any | null>(null);
    const [mcqForm, setMcqForm] = useState<{
        question: string;
        description: string;
        marks: number;
        options: McqOptionDto[];
    }>({ question: '', description: '', marks: 1, options: [] });
    const [mcqImage, setMcqImage] = useState<File | null>(null);
    const [snackbar, setSnackbar] = useState<any>(null);

    // Identify current video
    const currentVideo = data?.find(item => item.url && !item.mcq);
    const videoCluster = data?.find(item => item.url);

    useEffect(() => {
        if (videoCluster) {
            setMeta({
                title: videoCluster.title || '',
                description: videoCluster.description || '',
                question: videoCluster.question || ''
            });
            if (videoCluster.url && !videoCluster.url.includes('firebasestorage')) {
                setUrlForm(videoCluster.url);
            }
        }
    }, [videoCluster]);

    // --- Video Handlers ---
    const handleUrlSubmit = async () => {
        if (!urlForm || !meta.title) return setMsg({ type: 'error', text: 'URL and Title required' });
        setLoadingVideo(true);
        try {
            await setMcqVideoByUrl(chapterId, {
                title: meta.title, url: urlForm, description: meta.description, question: meta.question, displayOrder: 1
            });
            setMsg({ type: 'success', text: 'Video updated' });
            refreshData();
        } catch (e) { setMsg({ type: 'error', text: 'Failed' }); }
        finally { setLoadingVideo(false); }
    };

    const handleFileSubmit = async () => {
        if (!file || !meta.title) return setMsg({ type: 'error', text: 'File and Title required' });
        setLoadingVideo(true); setProgress(0);
        try {
            await uploadMcqVideo(chapterId, file, meta, (p) => setProgress(p));
            setMsg({ type: 'success', text: 'Uploaded' });
            refreshData(); setFile(null);
        } catch (e) { setMsg({ type: 'error', text: 'Failed' }); }
        finally { setLoadingVideo(false); }
    };

    const handleDeleteVideo = async () => {
        if (!window.confirm("Remove Video?")) return;
        setLoadingVideo(true);
        try {
            await deleteMcqVideo(chapterId, true);
            setMsg({ type: 'success', text: 'Removed' });
            setMeta({ title: '', description: '', question: '' }); setUrlForm('');
            refreshData();
        } catch (e) { setMsg({ type: 'error', text: 'Failed' }); }
        finally { setLoadingVideo(false); }
    };

    // --- MCQ CRUD Handlers ---

    const handleOpenCreate = () => {
        setEditingGroup(null);
        setMcqForm({ question: '', description: '', marks: 1, options: [
                { text: '', correct: false, explanation: '' },
                { text: '', correct: false, explanation: '' },
                { text: '', correct: false, explanation: '' },
                { text: '', correct: false, explanation: '' }
            ]});
        setMcqImage(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (group: any) => {
        setEditingGroup(group);
        setMcqForm({
            question: group.question,
            description: group.description || '',
            marks: group.marks || 1,
            options: group.mcq ? group.mcq.map((o: any) => ({
                id: o.id, text: o.text, correct: o.correct, explanation: o.explanation || ''
            })) : []
        });
        setMcqImage(null);
        setOpenDialog(true);
    };

    const handleSaveMcq = async () => {
        try {
            // Validate Question
            if (!mcqForm.question) {
                alert("Question text is required");
                return;
            }

            const validOptions = mcqForm.options.filter(o => o.text.trim() !== '');

            // Validate Option Count
            if (validOptions.length < 2) {
                alert("At least 2 options are required");
                return;
            }

            // --- NEW VALIDATION: Check Correct Answer ---
            const correctCount = validOptions.filter(o => o.correct).length;
            if (correctCount !== 1) {
                alert(`Exactly one option must be marked as correct. You have marked ${correctCount}.`);
                return;
            }
            // ---------------------------------------------

            const payload = {
                question: mcqForm.question,
                description: mcqForm.description,
                marks: mcqForm.marks,
                options: validOptions
            };

            if (editingGroup) {
                await updateMcqGroupWithUpload(chapterId, editingGroup.id, payload, mcqImage);
                setSnackbar({ msg: 'MCQ Updated', type: 'success' });
            } else {
                await createMcqGroupWithUpload(chapterId, payload, mcqImage);
                setSnackbar({ msg: 'MCQ Created', type: 'success' });
            }
            setOpenDialog(false);
            refreshData();
        } catch (e) {
            console.error(e);
            setSnackbar({ msg: 'Operation Failed', type: 'error' });
        }
    };

    const handleDeleteGroup = async (groupId: number) => {
        if (!window.confirm("Delete this MCQ Group and all options?")) return;
        try {
            await deleteMcqGroup(chapterId, groupId);
            setSnackbar({ msg: 'Deleted', type: 'success' });
            refreshData();
        } catch (e) {
            setSnackbar({ msg: 'Delete Failed', type: 'error' });
        }
    };

    const updateOption = (idx: number, field: keyof McqOptionDto, value: any) => {
        const newOpts = [...mcqForm.options];
        newOpts[idx] = { ...newOpts[idx], [field]: value };
        setMcqForm({ ...mcqForm, options: newOpts });
    };

    const addOptionSlot = () => {
        setMcqForm({ ...mcqForm, options: [...mcqForm.options, { text: '', correct: false, explanation: '' }] });
    };

    const removeOptionSlot = (idx: number) => {
        const newOpts = mcqForm.options.filter((_, i) => i !== idx);
        setMcqForm({ ...mcqForm, options: newOpts });
    };

    // --- Renders ---

    const renderVideoSection = () => (
        <Grid container spacing={4} sx={{ mb: 6 }}>
            {/* Form */}
            <Grid size={{ xs: 12, md: currentVideo ? 7 : 12 }}>
                <StyledCard>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2, bgcolor: '#fafafa' }}>
                        <Tabs value={mode} onChange={(_, v) => setMode(v)} textColor="primary" indicatorColor="primary">
                            <Tab label="External URL" value="URL" icon={<YouTubeIcon fontSize="small"/>} iconPosition="start" />
                            <Tab label="Upload File" value="FILE" icon={<CloudUploadIcon fontSize="small"/>} iconPosition="start"/>
                        </Tabs>
                    </Box>
                    <CardContent sx={{ p: 4 }}>
                        {msg && <Alert severity={msg.type} sx={{ mb: 2 }}>{msg.text}</Alert>}

                        <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>VIDEO DETAILS</Typography>
                        <Stack spacing={2} sx={{ mt: 2, mb: 3 }}>
                            <TextField
                                fullWidth required label="Title"
                                value={meta.title} onChange={e => setMeta({...meta, title: e.target.value})}
                                InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon color="action"/></InputAdornment> }}
                            />
                            <TextField
                                fullWidth multiline rows={2} label="Description"
                                value={meta.description} onChange={e => setMeta({...meta, description: e.target.value})}
                                InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon color="action"/></InputAdornment> }}
                            />
                            <TextField
                                fullWidth label="Instructions / Question"
                                value={meta.question} onChange={e => setMeta({...meta, question: e.target.value})}
                                InputProps={{ startAdornment: <InputAdornment position="start"><QuestionIcon color="action"/></InputAdornment> }}
                            />
                        </Stack>

                        <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 2 }}>SOURCE</Typography>
                        {mode === 'URL' ? (
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth required label="Video URL"
                                    value={urlForm} onChange={e => setUrlForm(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon color="action"/></InputAdornment> }}
                                />
                                <GradientButton onClick={handleUrlSubmit} disabled={loadingVideo} startIcon={loadingVideo ? null : <CheckIcon/>}>
                                    {loadingVideo ? 'Saving...' : 'Set Video'}
                                </GradientButton>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Box sx={{ border: `2px dashed ${css.primaryAccent}`, borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#f4fbfb', cursor: 'pointer' }} component="label">
                                    <input type="file" hidden accept="video/*" onChange={e => setFile(e.target.files?.[0] || null)} />
                                    <CloudUploadIcon sx={{ fontSize: 40, color: css.primaryAccent, mb: 1 }} />
                                    <Typography color={css.primaryDark} fontWeight={600}>{file ? file.name : "Select Video File"}</Typography>
                                </Box>
                                {loadingVideo && <LinearProgress variant="determinate" value={progress} />}
                                <GradientButton onClick={handleFileSubmit} disabled={loadingVideo} startIcon={loadingVideo ? null : <CloudUploadIcon/>}>
                                    {loadingVideo ? 'Uploading...' : 'Upload & Save'}
                                </GradientButton>
                            </Stack>
                        )}
                    </CardContent>
                </StyledCard>
            </Grid>

            {/* Current Video Preview */}
            {currentVideo && (
                <Grid size={{ xs: 12, md: 5 }}>
                    <StyledCard sx={{ overflow: 'hidden' }}>
                        <Box sx={{ background: css.primary, p: 2, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VideoIcon />
                            <Typography variant="h6" fontWeight={700}>Current MCQ Video</Typography>
                        </Box>
                        <CardContent>
                            <Box sx={{ bgcolor: '#000', borderRadius: 2, overflow: 'hidden', aspectRatio: '16/9', mb: 2 }}>
                                <video controls style={{ width: '100%', height: '100%' }}>
                                    <source src={currentVideo.url} />
                                </video>
                            </Box>
                            <Typography variant="subtitle1" fontWeight={700}>{currentVideo.title}</Typography>
                            {currentVideo.description && <Typography variant="body2" color="text.secondary">{currentVideo.description}</Typography>}
                            {currentVideo.question && (
                                <Box mt={1} p={1} bgcolor="#f5f5f5" borderRadius={1}>
                                    <Typography variant="caption" fontWeight={700}>INSTRUCTIONS</Typography>
                                    <Typography variant="body2">{currentVideo.question}</Typography>
                                </Box>
                            )}
                            <Button color="error" variant="outlined" startIcon={<DeleteIcon/>} onClick={handleDeleteVideo} sx={{ mt: 2 }} fullWidth>
                                Remove Video
                            </Button>
                        </CardContent>
                    </StyledCard>
                </Grid>
            )}
        </Grid>
    );

    return (
        <Box sx={{ animation: 'fadeIn 0.3s ease-in' }}>
            {/* 1. MCQ Video Management Section */}
            {renderVideoSection()}

            {/* 2. MCQ Groups Section */}
            <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
                <Typography variant="h6" fontWeight={700} color={css.primary}>MCQ Questions</Typography>
                <GradientButton startIcon={<AddIcon />} onClick={handleOpenCreate}>Add New MCQ</GradientButton>
            </Box>

            <Stack spacing={3}>
                {data?.map((section, idx) => (
                    <Box key={idx}>
                        {section.mcqGroups?.map((group: any) => (
                            <Card key={group.id} variant="outlined" sx={{ mb: 2, borderColor: '#e0e0e0' }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontWeight={600} fontSize="1.1rem">{group.question} ({group.marks} marks)</Typography>
                                        <Box>
                                            <IconButton size="small" onClick={() => handleOpenEdit(group)} color="primary"><EditIcon /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteGroup(group.id)}><DeleteIcon /></IconButton>
                                        </Box>
                                    </Box>
                                    <Grid container spacing={1} mt={1}>
                                        {group.mcq?.map((opt: any) => (
                                            <Grid size={{ xs: 12, sm: 6 }} key={opt.id}>
                                                <Paper sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', bgcolor: opt.correct ? '#e8f5e9' : '#fafafa', border: '1px solid', borderColor: opt.correct ? '#c8e6c9' : '#eee' }}>
                                                    {opt.correct ? <CorrectIcon color="success" fontSize="small"/> : <WrongIcon color="disabled" fontSize="small"/>}
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={opt.correct ? 600 : 400}>{opt.text}</Typography>
                                                        {opt.explanation && <Typography variant="caption" color="text.secondary" display="block">{opt.explanation}</Typography>}
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                ))}
                {(!data || data.length === 0) && (
                    <Paper sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderColor: '#ccc' }}>
                        <Typography color="text.secondary">No MCQ Groups found. Add one below.</Typography>
                    </Paper>
                )}
            </Stack>

            {/* CREATE / EDIT DIALOG */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#fafafa', borderBottom: '1px solid #eee' }}>
                    {editingGroup ? 'Edit MCQ Question' : 'Create New MCQ Question'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <TextField
                                fullWidth multiline rows={2} label="Question Text"
                                value={mcqForm.question} onChange={e => setMcqForm({ ...mcqForm, question: e.target.value })}
                            />
                            <TextField
                                fullWidth margin="normal" label="Description / Context"
                                value={mcqForm.description} onChange={e => setMcqForm({ ...mcqForm, description: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth type="number" label="Marks"
                                value={mcqForm.marks} onChange={e => setMcqForm({ ...mcqForm, marks: Number(e.target.value) })}
                            />
                            <Box mt={2} p={2} border="1px dashed #ccc" borderRadius={2} textAlign="center">
                                <Typography variant="caption" display="block" gutterBottom>Question Image (Optional)</Typography>
                                <Button component="label" size="small" startIcon={<ImageIcon/>}>
                                    {mcqImage ? 'Change' : 'Upload'}
                                    <input type="file" hidden accept="image/*" onChange={e => setMcqImage(e.target.files?.[0] || null)} />
                                </Button>
                                {mcqImage && <Typography variant="caption" display="block">{mcqImage.name}</Typography>}
                            </Box>
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 700, color: css.primary }}>OPTIONS</Typography>
                    <Stack spacing={1}>
                        {mcqForm.options.map((opt, idx) => (
                            <Paper key={idx} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                    checked={opt.correct}
                                    onChange={e => updateOption(idx, 'correct', e.target.checked)}
                                    color="success"
                                />
                                <Box flex={1}>
                                    <TextField
                                        fullWidth size="small" placeholder={`Option ${idx + 1}`}
                                        value={opt.text} onChange={e => updateOption(idx, 'text', e.target.value)}
                                        sx={{ mb: 0.5 }}
                                    />
                                    <TextField
                                        fullWidth size="small" placeholder="Explanation (Optional)" variant="standard"
                                        value={opt.explanation || ''} onChange={e => updateOption(idx, 'explanation', e.target.value)}
                                        InputProps={{ style: { fontSize: '0.8rem' } }}
                                    />
                                </Box>

                                <IconButton size="small" color="error" onClick={() => removeOptionSlot(idx)}>
                                    <DeleteOutline />
                                </IconButton>
                            </Paper>
                        ))}
                    </Stack>
                    <Button startIcon={<AddIcon />} onClick={addOptionSlot} sx={{ mt: 1 }}>Add Option</Button>

                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveMcq} disabled={!mcqForm.question}>
                        Save MCQ
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)}>
                <Alert severity={snackbar?.type}>{snackbar?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}